import { createQuery, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { toast } from "solid-sonner";
import {
  IconBot,
  IconBrandTelegram,
  IconCheck,
  IconCpu,
  IconLink,
  IconSettings,
  IconUnlink,
} from "~/components/icons";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { DropdownMenuItem } from "~/components/ui/dropdown-menu";
import {
  Switch,
  SwitchControl,
  SwitchLabel,
  SwitchThumb,
} from "~/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { getBillingUsage } from "~/features/billing/billing.api";
import { businessesResource } from "~/features/business/business.resource";
import { DocumentTypes } from "~/features/document/document.schema";
import {
  generateTelegramLinkToken,
  unlinkTelegramAccount,
} from "~/features/telegram-link/telegram-link.api";
import { telegramLinkStatusResource } from "~/features/telegram-link/telegram-link.resource";
import {
  getAllWorkflowConfigs,
  updateWorkflowConfig,
} from "~/features/workflow/workflow.api";
import {
  getDefaultStepsForDocType,
  type StepConfig,
  type WorkflowStep,
} from "~/features/workflow/workflow.schema";
import { cn, formatDisplayDate } from "~/lib/utils";

// ── Step icon helper ─────────────────────────────────────────────────────────

// (removed — steps identified by number badge instead)

// ── Workflow config panel ─────────────────────────────────────────────────────

function WorkflowConfigPanel(props: {
  allConfigs: Record<string, StepConfig[]> | undefined;
  businessId: string;
}) {
  const qc = useQueryClient();

  const handleToggleGroup = async (
    entityType: string,
    steps: WorkflowStep[],
    enabled: boolean,
  ) => {
    try {
      await Promise.all(
        steps.map((step) =>
          updateWorkflowConfig({
            data: {
              businessId: props.businessId,
              entityType,
              step,
              enabled,
            },
          }),
        ),
      );
      await qc.invalidateQueries({
        queryKey: ["all-workflow-configs", props.businessId],
      });
    } catch {
      toast.error("Failed to update workflow configuration");
    }
  };

  // Logical groupings shown as columns in the table. AI extraction runs for
  // every document type (the type isn't known until it runs), so it isn't
  // configurable. similar_txn / create_txn / link_txn aren't implemented yet.
  const GROUPS = [
    {
      key: "vendor_match" as const,
      label: "Vendor Lookup",
      short: "Vendor",
      description: "Match or create the vendor from extracted data",
      steps: ["vendor_match" as WorkflowStep],
    },
  ] as const;

  return (
    <div class="space-y-3 px-0 pt-4 pb-6">
      <p class="text-xs text-muted-foreground">
        Configure which workflow steps run automatically for each document type.
        OCR and AI extraction always run — the document type isn't known until
        extraction completes. You can also configure this per document from its
        Workflow tab.
      </p>
      <Show when={props.allConfigs}>
        <div class="overflow-x-auto rounded-md border">
          <table class="w-full text-sm">
            <thead class="border-b bg-muted/50">
              <tr>
                <th class="w-32 px-3 py-2 text-left font-medium">
                  Document Type
                </th>
                <For each={GROUPS}>
                  {(group) => (
                    <th
                      class="w-28 px-3 py-2 text-center font-medium"
                      title={group.description}
                    >
                      <span class="hidden sm:inline">{group.label}</span>
                      <span class="sm:hidden">{group.short}</span>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={DocumentTypes}>
                {(docType) => {
                  const steps = () =>
                    props.allConfigs?.[docType.value] ??
                    getDefaultStepsForDocType(docType.value);
                  return (
                    <tr class="border-b last:border-0">
                      <td class="px-3 py-2 font-medium whitespace-nowrap">
                        {docType.label}
                      </td>
                      <For each={GROUPS}>
                        {(group) => {
                          const masterEnabled = () =>
                            steps().find(
                              (c: StepConfig) => c.step === group.steps[0],
                            )?.enabled ?? false;
                          return (
                            <td class="px-3 py-2 text-center">
                              <Switch
                                checked={masterEnabled()}
                                onChange={(val) =>
                                  void handleToggleGroup(
                                    docType.value,
                                    group.steps as unknown as WorkflowStep[],
                                    val,
                                  )
                                }
                              >
                                <SwitchControl>
                                  <SwitchThumb />
                                </SwitchControl>
                                <SwitchLabel class="sr-only">
                                  {group.label}
                                </SwitchLabel>
                              </Switch>
                            </td>
                          );
                        }}
                      </For>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  );
}

// ── Usage helpers ────────────────────────────────────────────────────────────

function formatCredits(n: number): string {
  n = Math.round(n); // display only — ledger/meter keep exact fractional credits
  const compact = (v: number, suffix: string) =>
    `${v.toFixed(1).replace(/\.0$/, "")}${suffix}`;
  if (n >= 1_000_000) return compact(n / 1_000_000, "M");
  if (n >= 1_000) return compact(n / 1_000, "K");
  return n.toLocaleString();
}

function formatCost(usd: number): string {
  if (usd < 0.01) return `~$${usd.toFixed(4)}`;
  return `~$${usd.toFixed(2)}`;
}

const CREDIT_PRICE_USD = 0.0001; // per AI credit

// Plausible placeholder values shown blurred before real usage loads.
// Module-level so SSR and client render the same markup — no hydration mismatch.
const placeholderUsage = {
  storageBytes: 1_073_741_824, // 1.00 GB — clearly different from real value
  aiCredits: 250_000, // 250.0K — clearly different from real value
  aiCreditAllowance: 1000, // 1.0K — clearly different from real value
  storageQuotaBytes: 1 * 1024 * 1024 * 1024, // 1 GB
  tier: "free" as const,
};

export const Route = createFileRoute("/_dashboard/dashboard/settings/ai")({
  staticData: {
    breadcrumb: "AI",
  },
  loader: async ({ context }) => {
    // SSR-critical: the active business id gates the workflow config query.
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    // Prefetch the page's component-level queries so they resolve from cache
    // on navigation instead of flashing loading states. Fire-and-forget (not
    // awaited) so they never block the loader's critical path or SSR.
    void context.queryClient.prefetchQuery({
      queryKey: ["all-workflow-configs", businessId] as const,
      queryFn: () => getAllWorkflowConfigs({ data: { businessId } }),
    });
    void context.queryClient.prefetchQuery({
      queryKey: ["billing-usage"] as const,
      queryFn: () => getBillingUsage(),
    });
    void context.queryClient.prefetchQuery(
      telegramLinkStatusResource.options(undefined),
    );

    return { businessId };
  },
  component: AISettingsPage,
});

function AISettingsPage() {
  const qc = useQueryClient();
  const telegramStatusQuery = telegramLinkStatusResource.use(() => undefined);
  const telegramStatus = () =>
    telegramStatusQuery.data ?? {
      linked: false,
      telegramUsername: null,
      linkedAt: null,
    };

  const [linking, setLinking] = createSignal(false);
  const [unlinking, setUnlinking] = createSignal(false);

  // Eagerly fetch workflow configs so data is ready when the Workflow tab is opened.
  const businessesQuery = businessesResource.use(() => undefined);
  const businessId = () => businessesQuery.data?.activeBusinessId ?? "";
  const configsQuery = createQuery(() => ({
    queryKey: ["all-workflow-configs", businessId()] as const,
    queryFn: () =>
      getAllWorkflowConfigs({ data: { businessId: businessId() } }),
    enabled: !!businessId(),
  }));
  const allConfigs = () => configsQuery.data;

  // Lazy-loaded usage — fetched via a query so the loader's prefetch (same
  // query key) resolves it from cache on navigation instead of firing fresh.
  const usageQuery = createQuery(() => ({
    queryKey: ["billing-usage"] as const,
    queryFn: () => getBillingUsage(),
  }));
  const usageData = () => usageQuery.data ?? null;
  const displayUsage = () => usageData() ?? placeholderUsage;
  const isFreeTier = () => displayUsage().tier === "free";
  const creditsRemaining = () =>
    Math.max(0, displayUsage().aiCreditAllowance - displayUsage().aiCredits);

  // Per-surface credit breakdown (agent / telegram / document extraction…).
  // Only surfaces with usage are shown; hidden entirely while loading.
  const SOURCE_LABELS: Record<string, string> = {
    sidebar_agent: "Agent",
    telegram_agent: "Agent",
    telegram_summary: "Agent",
    document_extraction: "Doc Analysis",
    chat_auto_title: "Agent",
  };
  const usageBySource = () => {
    const bySource = usageData()?.aiCreditsBySource;
    if (!bySource) return [];
    // Merge sources sharing a label (all agent surfaces → "Agent").
    const byLabel = new Map<string, number>();
    for (const [source, credits] of Object.entries(bySource)) {
      const label = SOURCE_LABELS[source] ?? source;
      byLabel.set(label, (byLabel.get(label) ?? 0) + credits);
    }
    return [...byLabel.entries()]
      .filter(([, credits]) => credits > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([label, credits]) => ({ label, credits }));
  };

  async function handleConnectTelegram() {
    const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as
      | string
      | undefined;

    if (!botUsername) {
      toast.error(
        "Telegram bot is not configured. Contact your administrator.",
      );
      return;
    }

    setLinking(true);
    try {
      const { token } = await generateTelegramLinkToken();
      const deepLink = `https://t.me/${botUsername}?start=${token}`;
      window.open(deepLink, "_blank", "noopener,noreferrer");
      toast.success(
        "Telegram opened! Send the /start message to complete linking.",
      );

      setTimeout(() => {
        telegramLinkStatusResource.invalidate(qc, undefined);
      }, 5000);
    } catch {
      toast.error("Failed to generate link. Please try again.");
    } finally {
      setLinking(false);
    }
  }

  async function handleUnlinkTelegram() {
    if (!telegramStatus().linked) return;

    setUnlinking(true);
    try {
      await unlinkTelegramAccount();
      await telegramLinkStatusResource.invalidate(qc, undefined);
      toast.success("Telegram account unlinked.");
    } catch {
      toast.error("Failed to unlink Telegram. Please try again.");
    } finally {
      setUnlinking(false);
    }
  }

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-10">
      <StandardRouteDetailHeader
        icon={<IconBot class="size-5" />}
        title="AI Settings"
        menuContent={
          <DropdownMenuItem
            disabled={linking() || unlinking()}
            onClick={() =>
              void (telegramStatus().linked
                ? handleUnlinkTelegram()
                : handleConnectTelegram())
            }
          >
            <Show
              when={telegramStatus().linked}
              fallback={<IconLink class="size-4" />}
            >
              <IconUnlink class="size-4" />
            </Show>
            {telegramStatus().linked
              ? unlinking()
                ? "Unlinking Telegram..."
                : "Unlink Telegram"
              : linking()
                ? "Connecting Telegram..."
                : "Connect Telegram"}
          </DropdownMenuItem>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <StandardRouteDetailCard
              icon={<IconCpu class="size-5" />}
              title="Model Usage"
              contentClass="divide-y divide-border"
            >
              {/* Credit usage */}
              <div class="py-5 space-y-5">
                <div class="flex items-center gap-3.5">
                  <IconCpu class="size-4 text-muted-foreground shrink-0" />
                  <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                    <p class="text-xs text-muted-foreground">AI credits used</p>
                    <div
                      class={cn(
                        "transition-[filter] duration-300",
                        !usageData() && "blur-xs",
                      )}
                    >
                      <Show
                        when={isFreeTier()}
                        fallback={
                          <>
                            <p class="text-sm font-semibold">
                              {formatCredits(displayUsage().aiCredits)} /{" "}
                              {formatCredits(displayUsage().aiCreditAllowance)}{" "}
                              included
                            </p>
                            <Show
                              when={
                                Math.max(
                                  0,
                                  displayUsage().aiCredits -
                                    displayUsage().aiCreditAllowance,
                                ) > 0
                              }
                            >
                              <p class="text-xs text-muted-foreground">
                                {formatCost(
                                  Math.max(
                                    0,
                                    displayUsage().aiCredits -
                                      displayUsage().aiCreditAllowance,
                                  ) * CREDIT_PRICE_USD,
                                )}{" "}
                                · $0.0001/credit overage
                              </p>
                            </Show>
                          </>
                        }
                      >
                        <p class="text-sm font-semibold">
                          {formatCredits(displayUsage().aiCredits)} /{" "}
                          {formatCredits(displayUsage().aiCreditAllowance)}{" "}
                          credits
                        </p>
                        <div class="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            class="h-full rounded-full bg-primary transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                (displayUsage().aiCredits /
                                  displayUsage().aiCreditAllowance) *
                                  100,
                              )}%`,
                            }}
                          />
                        </div>
                        <p class="mt-2 text-xs text-muted-foreground">
                          {formatCredits(creditsRemaining())} remaining · resets
                          monthly
                        </p>
                      </Show>
                    </div>
                  </div>
                </div>

                {/* Per-surface breakdown — where the credits went.
                    Skeleton while loading so the card height doesn't jump. */}
                <Show
                  when={usageData() && usageBySource().length > 0}
                  fallback={
                    <Show when={!usageData()}>
                      <div class="rounded-md bg-muted/50 px-4 py-4 space-y-2.5">
                        <div class="h-3 w-24 animate-pulse rounded bg-muted" />
                        <div class="flex items-center justify-between gap-3">
                          <div class="h-3 w-20 animate-pulse rounded bg-muted" />
                          <div class="h-3 w-8 animate-pulse rounded bg-muted" />
                        </div>
                        <div class="flex items-center justify-between gap-3">
                          <div class="h-3 w-16 animate-pulse rounded bg-muted" />
                          <div class="h-3 w-8 animate-pulse rounded bg-muted" />
                        </div>
                      </div>
                    </Show>
                  }
                >
                  <div class="rounded-md bg-muted/50 px-4 py-4 space-y-2.5">
                    <p class="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                      Usage by feature
                    </p>
                    <For each={usageBySource()}>
                      {(item) => (
                        <div class="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                          <span class="min-w-0 truncate">{item.label}</span>
                          <span
                            class={cn(
                              "shrink-0 tabular-nums transition-[filter] duration-300",
                              !usageData() && "blur-xs",
                            )}
                          >
                            {formatCredits(item.credits)}
                          </span>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </div>

              {/* Cost estimate — only meaningful for metered (paid) usage */}
              <Show
                when={
                  !isFreeTier() &&
                  Math.max(
                    0,
                    displayUsage().aiCredits - displayUsage().aiCreditAllowance,
                  ) > 0
                }
              >
                <div class="py-5">
                  <div class="rounded-md bg-muted/50 px-4 py-4 space-y-2.5">
                    <div class="flex items-center justify-between text-xs text-muted-foreground">
                      <span>AI credits overage</span>
                      <span
                        class={cn(
                          "transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().aiCredits -
                              displayUsage().aiCreditAllowance,
                          ) * CREDIT_PRICE_USD,
                        )}
                      </span>
                    </div>
                    <div class="border-t border-border/60 pt-3 flex items-center justify-between">
                      <span class="text-sm font-semibold">Est. total</span>
                      <span
                        class={cn(
                          "text-sm font-bold transition-[filter] duration-300",
                          !usageData() && "blur-xs",
                        )}
                      >
                        {formatCost(
                          Math.max(
                            0,
                            displayUsage().aiCredits -
                              displayUsage().aiCreditAllowance,
                          ) * CREDIT_PRICE_USD,
                        )}
                        /mo
                      </span>
                    </div>
                  </div>
                </div>
              </Show>
            </StandardRouteDetailCard>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader class="pb-0">
                <Tabs defaultValue="workflow">
                  <TabsList class="w-full">
                    <TabsTrigger value="workflow" class="flex-1">
                      <IconSettings class="mr-2 size-4" />
                      Workflow
                    </TabsTrigger>
                    <TabsTrigger value="telegram" class="flex-1">
                      <IconBrandTelegram class="mr-2 size-4" />
                      Telegram
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="workflow">
                    <WorkflowConfigPanel
                      allConfigs={allConfigs()}
                      businessId={businessId()}
                    />
                  </TabsContent>

                  <TabsContent value="telegram">
                    <CardContent class="px-0 pt-4 pb-6 space-y-3">
                      <div class="flex items-center justify-between gap-3">
                        <div class="min-w-0">
                          <p class="text-sm font-medium">Connection status</p>
                          <p class="text-xs text-muted-foreground">
                            Link Telegram so you can chat with your assistant
                            from anywhere.
                          </p>
                        </div>
                        <Show
                          when={telegramStatus().linked}
                          fallback={
                            <Badge variant="secondary">Not linked</Badge>
                          }
                        >
                          <Badge
                            variant="default"
                            class="flex items-center gap-1 shrink-0"
                          >
                            <IconCheck class="size-3" />
                            Linked
                          </Badge>
                        </Show>
                      </div>

                      <Show when={telegramStatus().linked}>
                        <div class="rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
                          <Show when={telegramStatus().telegramUsername}>
                            <span>
                              Connected as{" "}
                              <span class="font-medium text-foreground">
                                @{telegramStatus().telegramUsername}
                              </span>
                            </span>
                          </Show>
                          <Show when={telegramStatus().linkedAt}>
                            <span class="ml-2 opacity-60">
                              &middot; linked{" "}
                              {formatDisplayDate(telegramStatus().linkedAt) ??
                                "-"}
                            </span>
                          </Show>
                        </div>
                      </Show>

                      <Show
                        when={telegramStatus().linked}
                        fallback={
                          <Button
                            onClick={() => void handleConnectTelegram()}
                            disabled={linking() || unlinking()}
                            variant="default"
                            class="w-full flex items-center gap-2"
                          >
                            <IconLink class="size-4" />
                            {linking() ? "Connecting..." : "Connect Telegram"}
                          </Button>
                        }
                      >
                        <Button
                          onClick={() => void handleUnlinkTelegram()}
                          disabled={linking() || unlinking()}
                          variant="destructive"
                          class="w-full flex items-center gap-2"
                        >
                          <IconUnlink class="size-4" />
                          {unlinking() ? "Unlinking..." : "Unlink Telegram"}
                        </Button>
                      </Show>

                      <p class="text-xs text-muted-foreground">
                        After clicking, Telegram opens. Send the /start message
                        to complete the link. The link expires in 15 minutes.
                      </p>
                    </CardContent>
                  </TabsContent>
                </Tabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>
    </div>
  );
}
